import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useHistory, useParams } from 'react-router-dom';

import {
  TEACHERS_BULK_UPLOAD_WORK_UPLOAD_AND_QR_URL,
  TEACHERS_PERMISSION_DENIED_URL,
  TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
} from 'config/urls';
import { isNil } from 'lodash';
import { includes } from 'lodash';
import {
  classroomInsightsStartProcessing,
  useInsightsClassroomTrackerInstanceSectionDetail,
  useLiveClassroomTrackerInstanceSectionStudentsProgress,
  useTrackerInstanceSectionClassroomCommonIssues,
  useTrackerInstanceSectionClassroomInsightsStatus,
  useTrackerInstanceSectionClassroomTomorrowFocus,
  useTrackerInstanceSectionStudentWorksCount
} from 'sdk';

import {
  GENERAL_CONCEPTUAL_UNDERSTANDING,
  SIGNIFICANT_ERRORS_IN_WORK,
  UNABLE_TO_DIAGNOSE,
  WORK_IS_COMPLETE_AND_CORRECT
} from 'pages/Teachers/Insights/constants';
import { useNavbar } from 'pages/Teachers/shared/TeacherPageLayout';
import { buildFilters } from 'utils/filters';
import { trackMixpanelEvent } from 'utils/integrations/mixpanel';
import { notifyErrors } from 'utils/notifications';

import Header from './components/Header';
import LearningGaps from './components/LearningGaps';
import StudentProgress from './components/StudentProgress';
import TomorrowsFocus from './components/TomorrowsFocus';
import UserContext from 'components/UserContext';

import styles from './style.module.scss';

const ClassroomInsights = () => {
  const { user } = useContext(UserContext);
  const [isLoadingInsights, setIsLoadingInsights] = useState(false);
  const [isGeneratingAfterError, setIsGeneratingAfterError] = useState(false);
  const [hasClassroomInitialCall, setHasClassroomInitialCall] = useState(false);
  const [filters, setFilters] = useState({
    names: []
  });
  const [allStudents, setAllStudents] = useState(null);

  const history = useHistory();
  const { trackerInstanceSectionId } = useParams();

  const { data: sectionDetails, loading } =
    useInsightsClassroomTrackerInstanceSectionDetail({
      trackerInstanceSectionId
    });

  const { data } = useTrackerInstanceSectionStudentWorksCount({
    trackerInstanceSectionId
  });

  const { navigation } = useNavbar({ title: '' });
  const {
    data: studentsProgressData,
    refetch: refetchStudentsProgress,
    isLoading: isLoadingStudentsProgress
  } = useLiveClassroomTrackerInstanceSectionStudentsProgress({
    trackerInstanceSectionId,
    filters: buildFilters(filters)
  });

  const {
    data: tomorrowsFocus,
    refetch: refetchTomorrowFocus,
    isLoading: isLoadingTomorrowFocus
  } = useTrackerInstanceSectionClassroomTomorrowFocus({
    trackerInstanceSectionId
  });

  useEffect(() => {
    navigation.setNoTopNavigation(true);
  }, [navigation]);

  useEffect(() => {
    if (isNil(studentsProgressData) || !isNil(allStudents)) return;
    setAllStudents(studentsProgressData);
  }, [studentsProgressData, allStudents]);

  useEffect(() => {
    if (isNil(data)) return;
    if (isNil(sectionDetails)) return;
    if (data.student_works_count === 0) {
      trackMixpanelEvent(
        user.id,
        'Live classroom insights empty state encountered.',
        {
          feature: 'live classroom insights',
          view: 'live classroom insights',
          class_id: sectionDetails.section_id,
          normalizedURL:
            process.env.REACT_APP_BASE_FRONTEND_URL +
            TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
        }
      );
      history.push(TEACHERS_BULK_UPLOAD_WORK_UPLOAD_AND_QR_URL);
    }
  }, [data, history, user.id, sectionDetails]);

  useEffect(() => {
    if (isNil(sectionDetails)) return;
    if (sectionDetails.deleted) {
      history.push(TEACHERS_PERMISSION_DENIED_URL);
    }
  }, [sectionDetails, history]);

  const { data: insightsStatus, refetch: refetchInsightsStatus } =
    useTrackerInstanceSectionClassroomInsightsStatus({
      trackerInstanceSectionId
    });

  const {
    data: commonIssues,
    refetch: refetchCommonIssues,
    isLoading: isLoadingCommonIssues
  } = useTrackerInstanceSectionClassroomCommonIssues({
    trackerInstanceSectionId
  });

  useEffect(() => {
    if (typeof refetchInsightsStatus !== 'function') return;

    const interval = setInterval(() => {
      refetchInsightsStatus();
    }, 15000);

    return () => clearInterval(interval);
  }, [refetchInsightsStatus]);

  useEffect(() => {
    const interval = setInterval(() => {
      refetchStudentsProgress();
    }, 15000);
    return () => clearInterval(interval);
  }, [refetchStudentsProgress]);

  useEffect(() => {
    if (insightsStatus !== 'error') {
      setIsGeneratingAfterError(false);
    }

    if (insightsStatus === 'up_to_date' && isLoadingInsights === true) {
      setIsLoadingInsights(false);

      refetchCommonIssues();
      refetchTomorrowFocus();
    }
    if (
      insightsStatus === 'error' &&
      isLoadingInsights === true &&
      !isGeneratingAfterError
    ) {
      setIsLoadingInsights(false);
    }
  }, [
    insightsStatus,
    isLoadingInsights,
    refetchCommonIssues,
    refetchTomorrowFocus,
    isGeneratingAfterError
  ]);

  const aboveMasteryStudentCount = useMemo(() => {
    if (!studentsProgressData) return 0;

    return studentsProgressData.filter(
      (student) => student.tag_description === WORK_IS_COMPLETE_AND_CORRECT
    ).length;
  }, [studentsProgressData]);

  const aboveMasteryStudents = useMemo(() => {
    if (isNil(studentsProgressData)) return null;

    return studentsProgressData.filter(
      (student) => student.tag_description === WORK_IS_COMPLETE_AND_CORRECT
    );
  }, [studentsProgressData]);

  const atMasteryStudentCount = useMemo(() => {
    if (!studentsProgressData) return 0;

    return studentsProgressData.filter(
      (student) => student.tag_description === GENERAL_CONCEPTUAL_UNDERSTANDING
    ).length;
  }, [studentsProgressData]);

  const belowMasteryStudentCount = useMemo(() => {
    if (!studentsProgressData) return 0;

    return studentsProgressData.filter(
      (student) => student.tag_description === SIGNIFICANT_ERRORS_IN_WORK
    ).length;
  }, [studentsProgressData]);

  const needsReviewStudentCount = useMemo(() => {
    if (!studentsProgressData) return 0;
    return studentsProgressData.filter(
      (student) =>
        student.tag_description === UNABLE_TO_DIAGNOSE ||
        student.tag_description === 'Needs Review'
    ).length;
  }, [studentsProgressData]);

  const handleGenerateInsights = useCallback(async () => {
    setIsLoadingInsights(true);

    if (insightsStatus === 'error') {
      setIsGeneratingAfterError(true);
    }

    const { success, errors } = await classroomInsightsStartProcessing({
      trackerInstanceSectionId
    });

    if (!success) {
      notifyErrors(errors);
      setIsLoadingInsights(false);
    }
  }, [insightsStatus, trackerInstanceSectionId]);

  useEffect(() => {
    if (hasClassroomInitialCall) return;
    if (!insightsStatus) return;
    if (
      aboveMasteryStudentCount +
        belowMasteryStudentCount +
        atMasteryStudentCount ===
      0
    )
      return;

    if (insightsStatus === 'up_to_date') {
      setHasClassroomInitialCall(true);
      return;
    }

    handleGenerateInsights();
    setHasClassroomInitialCall(true);
  }, [
    insightsStatus,
    hasClassroomInitialCall,
    aboveMasteryStudentCount,
    belowMasteryStudentCount,
    atMasteryStudentCount,
    handleGenerateInsights
  ]);

  const loadingCount = useMemo(() => {
    if (!studentsProgressData) return 0;
    return studentsProgressData.filter(
      (student) => student.tag_description === 'Loading'
    ).length;
  }, [studentsProgressData]);

  const disableGenerateButton = includes(
    ['up_to_date', 'not_allowed'],
    insightsStatus
  );

  return (
    <div>
      <Header
        data={sectionDetails}
        dataIsLoading={loading}
        aboveCount={aboveMasteryStudentCount}
        atCount={atMasteryStudentCount}
        belowCount={belowMasteryStudentCount}
        needsReviewCount={needsReviewStudentCount}
        loadingCount={loadingCount}
        regenerateDisabled={
          isLoadingInsights || disableGenerateButton || isGeneratingAfterError
        }
        onRegenerateClick={handleGenerateInsights}
        onPlusButtonClick={() =>
          history.push(TEACHERS_BULK_UPLOAD_WORK_UPLOAD_AND_QR_URL)
        }
      />
      <div className={styles.container}>
        <div className={styles.main}>
          <div className={styles.layout}>
            <div className={styles.leftContainer}>
              <section className={styles.card}>
                <StudentProgress
                  students={studentsProgressData}
                  allStudents={allStudents}
                  isLoadingData={isLoadingStudentsProgress}
                  onPublishSuccess={refetchStudentsProgress}
                  sectionId={sectionDetails?.section_id}
                  filters={filters}
                  setFilters={setFilters}
                />
              </section>

              <section className={styles.card}>
                <LearningGaps
                  commonIssues={commonIssues}
                  isLoading={
                    isLoadingCommonIssues ||
                    ['processing', 'not_allowed'].includes(insightsStatus) ||
                    isLoadingInsights
                  }
                />
              </section>
            </div>
            <section className={styles.card}>
              <TomorrowsFocus
                celebrationsStudents={aboveMasteryStudents}
                tomorrowsFocus={tomorrowsFocus}
                isLoading={
                  isLoadingTomorrowFocus ||
                  ['processing', 'not_allowed'].includes(insightsStatus) ||
                  isLoadingInsights
                }
              />
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassroomInsights;
